import React, { useRef, useState } from 'react';
import Modal from './Modal';
import BeGatewayPayment from './BeGatewayPayment';
import CustomPaymentForm from './CustomPaymentForm';

/**
 * Payment Modal component that displays BeGateway payment form in a modal
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the payment modal is open
 * @param {function} props.onClose - Function to call when the modal is closed
 * @param {string} props.orderId - Unique order identifier
 * @param {number} props.amount - Payment amount
 * @param {string} props.currency - Currency code (EUR, USD, etc)
 * @param {string} props.description - Payment description
 * @param {Object} props.customer - Customer information
 * @param {function} props.onSuccess - Callback for successful payment
 * @param {function} props.onError - Callback for payment errors
 */
const PaymentModal = ({
    isOpen,
    onClose,
    orderId,
    amount,
    currency = 'EUR',
    description = 'Star Map Purchase',
    customer = {},
    onSuccess,
    onError
}) => {
    const paymentRef = useRef(null);
    const [useCustomForm, setUseCustomForm] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentToken, setPaymentToken] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(true);
    const [tokenError, setTokenError] = useState(null);

    // Load payment token when modal opens
    React.useEffect(() => {
        if (isOpen && useCustomForm && !paymentToken) {
            fetchPaymentToken();
        }
    }, [isOpen, useCustomForm]);

    // Fetch payment token from the server
    const fetchPaymentToken = async () => {
        try {
            setTokenLoading(true);
            setTokenError(null);

            const paymentData = {
                amount,
                currency,
                description,
                order_id: orderId,
                customer
            };

            // Use the API subdomain directly for production
            const apiBaseUrl = window.location.hostname === 'localhost'
                ? '/api'
                : 'https://api.interstellarmap.com';

            const response = await fetch(`${apiBaseUrl}/payment/create-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(paymentData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to create payment token');
            }

            setPaymentToken(data);
            setTokenLoading(false);
        } catch (err) {
            console.error('Error creating payment token:', err);
            setTokenError(err.message || 'Failed to initialize payment');
            setTokenLoading(false);
            if (onError) onError(err);
        }
    };

    // Custom success handler to close modal
    const handleSuccess = (data) => {
        if (onSuccess) {
            onSuccess(data);
        }
        onClose();
    };

    // Custom error handler to close modal
    const handleError = (error) => {
        if (onError) {
            onError(error);
        }
        onClose();
    };

    // Format amount for display (cents to whole currency)
    const formatAmount = (amountInCents) => {
        return (amountInCents / 100).toFixed(2);
    };

    // Toggle between custom form and iframe
    const togglePaymentMethod = () => {
        setUseCustomForm(!useCustomForm);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Complete Your Payment"
            size="xl"
            showCloseButton={!isProcessing}
        >
            <div className="payment-modal-content">
                {/* Order summary */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500">Order #{orderId}</p>
                            <p className="text-gray-700">{description}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-lg font-semibold">{formatAmount(amount)} {currency}</p>
                        </div>
                    </div>
                </div>

                {useCustomForm ? (
                    // Custom payment form
                    <>
                        {tokenLoading ? (
                            <div className="bg-white rounded-lg p-6 flex flex-col items-center justify-center min-h-[400px]">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mb-4"></div>
                                <p className="text-gray-700">Initializing payment...</p>
                            </div>
                        ) : tokenError ? (
                            <div className="bg-white rounded-lg p-6">
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                                    {tokenError}
                                </div>
                                <button
                                    onClick={fetchPaymentToken}
                                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <CustomPaymentForm
                                onSubmit={handleSuccess}
                                onCancel={onClose}
                                amount={amount}
                                currency={currency}
                                paymentToken={paymentToken}
                                isProcessing={isProcessing}
                                setIsProcessing={setIsProcessing}
                            />
                        )}

                        {/* Option to switch to iframe */}
                        <div className="mt-4 text-sm text-center">
                            <button
                                onClick={togglePaymentMethod}
                                className="text-purple-600 hover:text-purple-800 underline"
                                disabled={isProcessing}
                            >
                                Use alternate payment method
                            </button>
                        </div>
                    </>
                ) : (
                    // BeGateway component with button hidden
                    <>
                        <div className="w-full mx-auto">
                            <BeGatewayPayment
                                ref={paymentRef}
                                orderId={orderId}
                                amount={amount}
                                currency={currency}
                                description={description}
                                customer={customer}
                                onSuccess={handleSuccess}
                                onError={handleError}
                                hideButton={true}
                            />
                        </div>

                        {/* Option to switch to custom form */}
                        <div className="mt-4 text-sm text-center">
                            <button
                                onClick={togglePaymentMethod}
                                className="text-purple-600 hover:text-purple-800 underline"
                            >
                                Use card payment form
                            </button>
                        </div>
                    </>
                )}

                <div className="mt-4 text-sm text-center text-gray-500">
                    <p>Your payment is secured using industry-standard encryption.</p>
                    {!isProcessing && (
                        <button
                            onClick={onClose}
                            className="mt-4 px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md"
                            disabled={isProcessing}
                        >
                            Cancel Payment
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default PaymentModal; 